/**
 * Detalle de una grabación (SDD §7.2): estado del pipeline, notas, hablantes,
 * transcripción, exportar, copiar para IA, compartir, recibo de borrado del
 * audio y consentimiento declarado.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import {
  ACTIVE_STATUSES,
  NO_RETRY_ERROR_CODES,
  recordingErrorLabel,
  type RecordingDetail,
  type Speaker
} from '@eco/shared'
import { useApp, useToast, errorText } from '../components/AppContext'
import { SendNotesDialog } from '../components/SendNotesDialog'
import { SpeakerStatsCard } from '../components/SpeakerStats'
import { TrackerChips } from '../components/Trackers'
import { AskPanel } from '../components/AskPanel'
import { NameHints } from '../components/NameHints'
import { NotesPanel } from '../components/NotesPanel'
import { ShareDialog } from '../components/ShareDialog'
import { SpeakersDialog } from '../components/SpeakersDialog'
import { TranscriptPanel, type TranscriptHandle } from '../components/TranscriptPanel'
import { ErrorBox, Menu, Modal, Skeleton, StatusBadge } from '../components/ui'
import {
  IconArrowLeft,
  IconChevronDown,
  IconCopy,
  IconDownload,
  IconMail,
  IconMore,
  IconShare,
  IconTrash
} from '../components/icons'
import { EcoApiError } from '../lib/api'
import { buildMarkdown, downloadText, exportFilename } from '../lib/exportMarkdown'
import { transcriptSrt, transcriptTxt, transcriptVtt } from '../lib/exportTranscript'
import { ADVERTENCIA_LABEL, formatDate, formatDuration } from '../lib/format'
import { useAsync, useDocumentTitle, usePolling } from '../lib/hooks'
import { speakerByName } from '../lib/transcript'

const PROGRESO: Record<string, string> = {
  recording: 'Grabando… las notas llegan unos minutos después de detener.',
  captured: 'Recibiendo el audio desde tu navegador…',
  queued: 'En cola para transcribir…',
  processing: 'Transcribiendo y separando hablantes…',
  merging: 'Ordenando la conversación…',
  summarizing: 'Escribiendo las notas…'
}

export function Detail() {
  const { id = '' } = useParams()
  const { api, refreshUsage, org, me } = useApp()
  const toast = useToast()
  const nav = useNavigate()
  const transcriptRef = useRef<TranscriptHandle>(null)
  const detail = useAsync(() => api.recording(id), [api, id])
  const d = detail.data
  useDocumentTitle(d?.recording.titulo)
  const projects = useAsync(() => api.projects(), [api])
  const orgCfg = useAsync(() => api.orgSettings(), [api])
  const [share, setShare] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)
  const [speakers, setSpeakers] = useState<{ open: boolean; focus: Speaker | null }>({
    open: false,
    focus: null
  })
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editingTitle, setEditingTitle] = useState<string | null>(null)
  // Escape cancela (el blur que sigue no guarda) y Enter + blur no guardan dos veces
  const titleDone = useRef(false)
  const [exporting, setExporting] = useState(false)
  const [acting, setActing] = useState(false)

  // Analítica: notas vistas (una vez por grabación y carga), con su origen
  const [searchParams] = useSearchParams()
  const origen = searchParams.get('origen') === 'correo' ? 'correo' : 'web'
  // `?t=ms`: abrir la reunión en un momento (citas de «Pregunta a tus reuniones», links)
  const tParam = Number(searchParams.get('t'))
  const jumped = useRef<string | null>(null)
  useEffect(() => {
    if (!d || !d.segments.length || !Number.isFinite(tParam) || tParam <= 0) return
    const key = `${d.recording.id}:${tParam}`
    if (jumped.current === key) return
    jumped.current = key
    // Tras el primer render de la lista virtual
    requestAnimationFrame(() => transcriptRef.current?.jumpTo(tParam))
  }, [d, tParam])
  const seen = useRef<string | null>(null)
  useEffect(() => {
    const s = d?.summaries.find((x) => x.estado === 'ok' || x.estado === 'editada')
    if (!d || !s || seen.current === d.recording.id) return
    seen.current = d.recording.id
    void api.event('notas_vistas', {
      origen,
      editadas: d.summaries.some((x) => x.estado === 'editada'),
      plantilla: s.templateId
    })
  }, [d, api, origen])

  const working =
    !!d &&
    ((ACTIVE_STATUSES as readonly string[]).includes(d.recording.estado) ||
      d.summaries.some((s) => s.estado === 'generating'))
  usePolling(
    () => {
      void detail.reload()
      if (d?.recording.estado === 'summarizing') void refreshUsage()
    },
    4000,
    working
  )

  if (detail.error) {
    const notFound = detail.error instanceof EcoApiError && detail.error.status === 404
    return (
      <div className="page narrow">
        {notFound ? (
          <div className="empty">
            <h2>Esta grabación no existe o fue eliminada</h2>
            <Link to="/" className="btn" style={{ marginTop: 12 }}>
              Volver a la biblioteca
            </Link>
          </div>
        ) : (
          <ErrorBox error={detail.error} onRetry={() => void detail.reload()} />
        )}
      </div>
    )
  }
  if (!d)
    return (
      <div className="page">
        <div className="stack">
          <Skeleton h={34} w="50%" />
          <Skeleton h={18} w="30%" />
          <Skeleton h={320} />
        </div>
      </div>
    )

  const r = d.recording
  const reload = () => detail.reload()
  // Plan Equipo: quien la grabó decide si se comparte; dueño y administradores también editan
  const enEquipo = org?.tipo === 'equipo'
  const esMia = r.ownerUserId === me?.user.id
  const canEdit = !enEquipo || esMia || org?.rol === 'owner' || org?.rol === 'admin'
  const hasNotes = d.summaries.some((s) => s.estado === 'ok' || s.estado === 'editada')
  const sharingOff = orgCfg.data?.compartirHabilitado === false
  // El correo manda un link a las notas: necesita notas y que compartir esté permitido
  const shareBlock =
    r.estado !== 'completed'
      ? 'Disponible cuando la reunión termine de procesarse'
      : sharingOff
        ? 'Compartir está desactivado en tu organización (Ajustes → Privacidad)'
        : null
  const sendBlock = shareBlock ?? (hasNotes ? null : 'Esta reunión aún no tiene notas')

  function editTitle() {
    titleDone.current = false
    setEditingTitle(r.titulo)
  }

  async function saveTitle() {
    if (titleDone.current) return
    titleDone.current = true
    const titulo = editingTitle?.trim()
    setEditingTitle(null)
    if (!titulo || titulo === r.titulo) return
    try {
      await api.patchRecording(r.id, { titulo })
      await reload()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function setVisibility(visibilidad: 'privada' | 'equipo') {
    try {
      await api.patchRecording(r.id, { visibilidad })
      await reload()
      toast(
        visibilidad === 'equipo'
          ? 'Compartida con el equipo'
          : 'Ahora es privada: solo tú la ves (y las carpetas del equipo, si está en una)'
      )
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function moveTo(projectId: string | null) {
    try {
      await api.patchRecording(r.id, { projectId })
      await reload()
      toast(projectId ? 'Movida al proyecto' : 'Quitada del proyecto')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function retry() {
    if (acting) return
    setActing(true)
    try {
      await api.retryRecording(r.id)
      toast('Reintentando…')
      await reload()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setActing(false)
    }
  }

  async function remove() {
    if (acting) return
    setActing(true)
    try {
      await api.deleteRecording(r.id)
      toast('Grabación eliminada')
      void refreshUsage()
      nav('/')
    } catch (e) {
      toast(errorText(e), 'error')
      setActing(false)
    }
  }

  function exportMd() {
    downloadText(exportFilename(r.titulo, r.fechaInicio, 'md'), buildMarkdown(d!))
  }
  async function exportPdf() {
    setExporting(true)
    try {
      const { downloadPdf } = await import('../lib/exportPdf')
      await downloadPdf(d!)
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setExporting(false)
    }
  }
  async function exportDocx() {
    setExporting(true)
    try {
      const { downloadDocx } = await import('../lib/exportDocx')
      await downloadDocx(d!)
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setExporting(false)
    }
  }
  function exportTranscript(formato: string) {
    const name = (ext: string) => exportFilename(r.titulo, r.fechaInicio, ext)
    if (formato === 'txt')
      downloadText(name('txt'), transcriptTxt(r.titulo, d!.segments), 'text/plain')
    else if (formato === 'srt')
      downloadText(name('srt'), transcriptSrt(d!.segments), 'application/x-subrip')
    else if (formato === 'vtt') downloadText(name('vtt'), transcriptVtt(d!.segments), 'text/vtt')
  }
  function copyForAi() {
    const md = buildMarkdown(d!)
    const prompt = `Estas son las notas y la transcripción de una reunión grabada con eco. Úsalas como contexto para responder mis preguntas.\n\n${md}`
    void navigator.clipboard
      .writeText(prompt)
      .then(() => toast('Copiado: pégalo en tu asistente de IA'))
      .catch(() => toast('No se pudo copiar. Revisa el permiso del portapapeles.', 'error'))
  }

  const openSpeaker = (name: string) =>
    setSpeakers({ open: true, focus: speakerByName(d.speakers, name) ?? null })

  return (
    <div className="page" style={{ maxWidth: 1280 }}>
      <Link to={r.projectId ? `/p/${r.projectId}` : '/'} className="back no-print">
        <IconArrowLeft /> {d.project?.nombre ?? 'Biblioteca'}
      </Link>
      <div className="detail-head">
        <div className="grow" style={{ minWidth: 280 }}>
          {editingTitle !== null ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void saveTitle()
              }}
            >
              <input
                autoFocus
                aria-label="Título"
                value={editingTitle}
                maxLength={200}
                onChange={(e) => setEditingTitle(e.target.value)}
                onBlur={() => void saveTitle()}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') return
                  titleDone.current = true
                  setEditingTitle(null)
                }}
                className="serif"
                style={{ fontSize: 26, width: '100%' }}
              />
            </form>
          ) : (
            <h1>
              {canEdit ? (
                <button
                  className="iconbtn"
                  style={{ font: 'inherit', color: 'inherit', padding: 0, textAlign: 'left' }}
                  title="Cambiar título"
                  onClick={editTitle}
                >
                  {r.titulo}
                </button>
              ) : (
                r.titulo
              )}
            </h1>
          )}
          <div className="detail-meta">
            <StatusBadge estado={r.estado} />
            <span>{formatDate(r.fechaInicio)}</span>
            {r.duracionMs > 0 && (
              <>
                <span className="sep" aria-hidden="true" />
                <span>{formatDuration(r.duracionMs)}</span>
              </>
            )}
            {(r.origen === 'import' || r.modo === 'presencial' || r.plataforma === 'meet') && (
              <>
                <span className="sep" aria-hidden="true" />
                <span>
                  {r.origen === 'import'
                    ? 'Audio importado'
                    : r.modo === 'presencial'
                      ? 'Presencial'
                      : 'Google Meet'}
                </span>
              </>
            )}
            <select
              aria-label="Proyecto"
              value={r.projectId ?? ''}
              disabled={!canEdit}
              onChange={(e) => void moveTo(e.target.value || null)}
            >
              <option value="">Sin proyecto</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
            {enEquipo &&
              (esMia ? (
                <button
                  className={`chip ${r.visibilidad === 'equipo' ? 'on' : ''}`}
                  aria-pressed={r.visibilidad === 'equipo'}
                  onClick={() =>
                    void setVisibility(r.visibilidad === 'equipo' ? 'privada' : 'equipo')
                  }
                  title={
                    r.visibilidad === 'equipo'
                      ? 'Todo el equipo la ve. Clic para que sea privada.'
                      : 'Solo tú la ves. Clic para compartirla con el equipo.'
                  }
                >
                  {r.visibilidad === 'equipo'
                    ? 'Compartida con el equipo'
                    : 'Privada · Compartir con el equipo'}
                </button>
              ) : (
                <span className="pill">Compartida por su autor · solo lectura</span>
              ))}
          </div>
        </div>
        <div className="detail-actions no-print">
          {canEdit && (
            <button
              className="btn primary"
              onClick={() => setShare(true)}
              disabled={!!shareBlock}
              title={shareBlock ?? undefined}
            >
              <IconShare /> Compartir
            </button>
          )}
          <Menu
            label="Exportar"
            button={
              <>
                <IconDownload /> Exportar <IconChevronDown className="chev" />
              </>
            }
          >
            {(close) => {
              const off = !d.segments.length
              const run = (fn: () => void) => () => {
                close()
                fn()
              }
              return (
                <>
                  <button role="menuitem" disabled={off} onClick={run(exportMd)}>
                    Markdown <span className="hint">.md</span>
                  </button>
                  <button
                    role="menuitem"
                    disabled={off || exporting}
                    onClick={run(() => void exportPdf())}
                  >
                    PDF <span className="hint">.pdf</span>
                  </button>
                  <button
                    role="menuitem"
                    disabled={off || exporting}
                    onClick={run(() => void exportDocx())}
                  >
                    Word <span className="hint">.docx</span>
                  </button>
                  <div className="sep" role="separator" />
                  <button
                    role="menuitem"
                    disabled={off}
                    onClick={run(() => exportTranscript('txt'))}
                  >
                    Transcripción en texto <span className="hint">.txt</span>
                  </button>
                  <button
                    role="menuitem"
                    disabled={off}
                    onClick={run(() => exportTranscript('srt'))}
                  >
                    Subtítulos <span className="hint">.srt</span>
                  </button>
                  <button
                    role="menuitem"
                    disabled={off}
                    onClick={run(() => exportTranscript('vtt'))}
                  >
                    Subtítulos web <span className="hint">.vtt</span>
                  </button>
                  <div className="sep" role="separator" />
                  <button
                    role="menuitem"
                    disabled={off}
                    onClick={run(copyForAi)}
                    title="Copia notas y transcripción para pegar en Claude u otra IA"
                  >
                    <IconCopy /> Copiar para IA
                  </button>
                </>
              )
            }}
          </Menu>
          {canEdit && (
            <Menu label="Más acciones" className="btn" button={<IconMore />}>
              {(close) => (
                <>
                  <button
                    role="menuitem"
                    disabled={!!sendBlock}
                    title={sendBlock ?? undefined}
                    onClick={() => {
                      close()
                      setSendOpen(true)
                    }}
                  >
                    <IconMail /> Enviar por correo
                  </button>
                  <div className="sep" role="separator" />
                  <button
                    role="menuitem"
                    className="danger"
                    aria-label="Eliminar grabación"
                    onClick={() => {
                      close()
                      setConfirmDelete(true)
                    }}
                  >
                    <IconTrash /> Eliminar
                  </button>
                </>
              )}
            </Menu>
          )}
        </div>
      </div>

      {canEdit && <StatusBanner detail={d} busy={acting} onRetry={() => void retry()} />}
      {canEdit && (
        <NameHints
          detail={d}
          onReview={() => setSpeakers({ open: true, focus: null })}
          onChanged={reload}
        />
      )}

      {r.advertencias.length > 0 && (
        <div className="notice warn" style={{ marginBottom: 14 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {r.advertencias.map((w) => (
              <li key={w}>{ADVERTENCIA_LABEL[w] ?? w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="detail-grid">
        <div className="stack">
          <NotesPanel
            readOnly={!canEdit}
            detail={d}
            onJump={(ms) => transcriptRef.current?.jumpTo(ms)}
            onChanged={reload}
          />
          {d.segments.length > 0 && (
            <AskPanel recordingId={r.id} onJump={(ms) => transcriptRef.current?.jumpTo(ms)} />
          )}
          <SpeakerStatsCard segments={d.segments} />
          <div className="card tight stack" style={{ gap: 8 }}>
            {d.speakers.length > 0 && (
              <div className="row wrap">
                <span className="faint" style={{ fontSize: 12 }}>
                  Hablantes
                </span>
                <div className="speakers">
                  {[...new Set(d.segments.map((s) => s.speaker))].map((name) => (
                    <button
                      key={name}
                      className="spk"
                      disabled={!canEdit}
                      onClick={() => openSpeaker(name)}
                      title={canEdit ? 'Renombrar o fusionar' : undefined}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <TrackerChips
              segments={d.segments}
              onJump={(ms) => transcriptRef.current?.jumpTo(ms)}
            />
            {r.marcas.length > 0 && (
              <div className="row wrap" style={{ gap: 6 }}>
                <span className="faint" style={{ fontSize: 12 }}>
                  Momentos marcados
                </span>
                {r.marcas.map((ms) => (
                  <button
                    key={ms}
                    className="ev"
                    style={{ marginLeft: 0 }}
                    onClick={() => transcriptRef.current?.jumpTo(ms)}
                  >
                    ★ {formatDuration(ms)}
                  </button>
                ))}
              </div>
            )}
            <Receipt detail={d} />
          </div>
        </div>
        <div>
          {d.segments.length ? (
            <TranscriptPanel
              ref={transcriptRef}
              segments={d.segments}
              offsetOthersMs={r.offsetOthersMs}
              onSpeakerClick={canEdit ? openSpeaker : undefined}
              marcas={r.marcas}
            />
          ) : (
            <div className="card muted">
              {r.estado === 'completed'
                ? 'No se detectó voz en esta grabación.'
                : 'La transcripción aparecerá aquí.'}
            </div>
          )}
        </div>
      </div>

      <ShareDialog open={share} onClose={() => setShare(false)} detail={d} onChanged={reload} />
      <SendNotesDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        recordingId={r.id}
        onSent={() => void reload()}
      />
      <SpeakersDialog
        open={speakers.open}
        focus={speakers.focus}
        onClose={() => setSpeakers({ open: false, focus: null })}
        detail={d}
        onChanged={reload}
      />
      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="¿Eliminar esta grabación?"
        footer={
          <>
            <button className="btn ghost" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </button>
            <button className="btn live" disabled={acting} onClick={() => void remove()}>
              Eliminar
            </button>
          </>
        }
      >
        <p>
          Se borran la transcripción, las notas y los links compartidos. Se elimina de inmediato de
          tu biblioteca y de forma definitiva de nuestros sistemas en 7 días.
        </p>
      </Modal>
    </div>
  )
}

function StatusBanner({
  detail,
  busy,
  onRetry
}: {
  detail: RecordingDetail
  busy: boolean
  onRetry: () => void
}) {
  const r = detail.recording
  if (r.estado === 'completed' || r.estado === 'aborted') return null
  if (r.estado === 'failed') {
    // Sin audio (se borra 24 h después del fallo) solo se puede reintentar si ya hay transcripción;
    // los fallos por duración no se arreglan reintentando
    const sinRemedio = !!r.errorCode && NO_RETRY_ERROR_CODES.includes(r.errorCode)
    const sinAudio = !!r.audioBorradoEn && detail.segments.length === 0
    const puedeReintentar = !sinRemedio && !sinAudio
    return (
      <div className="notice error row" style={{ marginBottom: 14 }}>
        <span className="grow">
          {recordingErrorLabel(r.errorCode)}
          {sinAudio &&
            !sinRemedio &&
            ' El audio ya se borró: si guardaste la copia local, impórtala.'}
        </span>
        {puedeReintentar ? (
          <button className="btn sm" disabled={busy} onClick={onRetry}>
            {busy ? 'Reintentando…' : 'Reintentar'}
          </button>
        ) : (
          <Link className="btn sm" to="/import">
            Importar
          </Link>
        )}
      </div>
    )
  }
  if (r.estado === 'retenida')
    return (
      <div className="notice warn row" style={{ marginBottom: 14 }}>
        <span className="grow">
          Te quedaste sin horas este mes. Guardamos el audio hasta 7 días: mejora tu plan y se
          procesa sola.
        </span>
        <Link className="btn sm primary" to="/plan">
          Ver planes
        </Link>
      </div>
    )
  if (r.estado === 'held')
    return (
      <div className="notice warn" style={{ marginBottom: 14 }}>
        Tenemos mucha demanda: tu grabación está en espera y se procesará en cuanto sea posible.
      </div>
    )
  return (
    <div className="notice row" style={{ marginBottom: 14 }} aria-live="polite">
      <span className="pill work">
        <span className="dot" />
      </span>
      <span className="grow">{PROGRESO[r.estado] ?? 'Procesando…'}</span>
    </div>
  )
}

function Receipt({ detail }: { detail: RecordingDetail }) {
  const r = detail.recording
  return (
    <div className="stack" style={{ gap: 4 }}>
      {r.audioBorradoEn ? (
        <div className="receipt">
          <span className="ok" aria-hidden="true">
            ✓
          </span>
          <span>Audio eliminado de nuestros servidores el {formatDate(r.audioBorradoEn)}.</span>
        </div>
      ) : (
        <div className="receipt faint">
          <span aria-hidden="true">○</span>
          <span>El audio se elimina de nuestros servidores apenas termina el procesamiento.</span>
        </div>
      )}
      <div className="receipt faint">
        <span aria-hidden="true">{r.consentimientoDeclaradoEn ? '✓' : '–'}</span>
        <span>
          {r.consentimientoDeclaradoEn
            ? `Declaraste haber avisado a los participantes (${formatDate(r.consentimientoDeclaradoEn)}).`
            : r.origen === 'import'
              ? 'Audio importado: sin declaración de aviso.'
              : 'Sin declaración de aviso a los participantes.'}
        </span>
      </div>
    </div>
  )
}
